import { Fragment, ReactNode } from 'react';
import { IEditorJsBlock, parseCommentBlocks } from '../../utils';

interface IRichTextCommentProps {
  comment?: string | null;
  className?: string;
}

const ALLOWED_TAGS: Record<string, keyof JSX.IntrinsicElements> = {
  B: 'b',
  STRONG: 'strong',
  I: 'i',
  EM: 'em',
  U: 'u',
  MARK: 'mark',
  S: 's',
  CODE: 'code',
  BR: 'br',
  A: 'a',
  SPAN: 'span',
};

function domNodeToReact(node: ChildNode, key: number): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const element = node as HTMLElement;
  const children = Array.from(element.childNodes).map((child, index) =>
    domNodeToReact(child, index)
  );

  const tag = ALLOWED_TAGS[element.tagName];

  if (!tag) {
    return children;
  }

  if (tag === 'br') {
    return <br key={key} />;
  }

  if (tag === 'a') {
    return (
      <a
        key={key}
        href={element.getAttribute('href') || '#'}
        target='_blank'
        rel='noreferrer'
      >
        {children}
      </a>
    );
  }

  const Tag = tag;

  return <Tag key={key}>{children}</Tag>;
}

function parseInlineHtml(html: string): ReactNode {
  if (!html) {
    return null;
  }

  const parser = new DOMParser();
  const parsedDocument = parser.parseFromString(html, 'text/html');

  return Array.from(parsedDocument.body.childNodes).map((node, index) =>
    domNodeToReact(node, index)
  );
}

interface IEditorJsListItem {
  content: string;
  meta?: { checked?: boolean; [key: string]: unknown };
  items?: IEditorJsListItem[];
}

function renderListItems(
  items: IEditorJsListItem[],
  style: 'ordered' | 'unordered' | 'checklist',
  key?: number | string
) {
  const ListTag = style === 'ordered' ? 'ol' : 'ul';

  return (
    <ListTag key={key} className='pl-5 list-outside'>
      {items.map((item, index) => (
        <li key={index} className={style === 'checklist' ? 'list-none' : undefined}>
          {style === 'checklist' && (
            <input
              type='checkbox'
              checked={!!item.meta?.checked}
              readOnly
              className='mr-2'
            />
          )}
          {parseInlineHtml(item.content)}
          {!!item.items?.length && renderListItems(item.items, style)}
        </li>
      ))}
    </ListTag>
  );
}

function renderBlock(block: IEditorJsBlock) {
  const text = (block.data?.text as string) || '';

  switch (block.type) {
    case 'header': {
      const level = (block.data?.level as number) || 2;
      const HeaderTag = `h${Math.min(Math.max(level, 1), 6)}` as keyof JSX.IntrinsicElements;

      return <HeaderTag key={block.id}>{parseInlineHtml(text)}</HeaderTag>;
    }

    case 'list': {
      const style = (block.data?.style as 'ordered' | 'unordered' | 'checklist') || 'unordered';
      const items = (block.data?.items as IEditorJsListItem[]) || [];

      return renderListItems(items, style, block.id);
    }

    case 'quote': {
      const caption = (block.data?.caption as string) || '';

      return (
        <blockquote key={block.id} className='border-l-2 pl-4 italic'>
          <p>{parseInlineHtml(text)}</p>
          {caption && <cite>{parseInlineHtml(caption)}</cite>}
        </blockquote>
      );
    }

    case 'delimiter':
      return <hr key={block.id} className='my-4' />;

    case 'code': {
      const code = (block.data?.code as string) || '';

      return (
        <pre key={block.id} className='whitespace-pre-wrap'>
          <code>{code}</code>
        </pre>
      );
    }

    case 'paragraph':
    default:
      if (!text) {
        return null;
      }

      return <p key={block.id}>{parseInlineHtml(text)}</p>;
  }
}

export function RichTextComment({ comment, className }: IRichTextCommentProps) {
  if (!comment) {
    return null;
  }

  const blocks = parseCommentBlocks(comment);

  if (!blocks) {
    return <p className={className}>{comment}</p>;
  }

  return (
    <Fragment>
      {blocks.map((block) => (
        <div className={className} key={block.id}>
          {renderBlock(block)}
        </div>
      ))}
    </Fragment>
  );
}
